import clsx from 'clsx';
import { memo, useContext } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';

import { Icon } from '../../../../../../common/icon';
import Tooltip from '../../../../../../common/tooltip';
import WithLink from '../../../../../../common/link/withLink';
import { useMobile } from '../../../../../../hooks/useMobile';
import { DrawerContext } from '../../drawer/drawerContext';
import { RADIUS } from '../../../../../../../styles/themes';

import { MenuItem as MenuItemProps } from '../interfaces';
import { convertToKebabCase } from '../../../../../../../constants/helpers/text';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    background: 'transparent',
    padding: theme.spacing(1, 2),
    // rounded highlight so the hovered item does not span the whole sidebar
    margin: theme.spacing(0, 1),
    borderRadius: RADIUS.md,
    '&:hover': {
      background: theme.pageLayout.drawer.navigation.menuItemHoverBgAlt
    }
  },
  title: {
    color: theme.palette.primary.contrastText,
    marginLeft: theme.spacing(1),
    whiteSpace: 'nowrap'
  },
  icon: {
    fontSize: theme.elements.icon.size.m
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  isSelected: {
    color: theme.palette.primary.light,
    background: 'transparent !important',
    cursor: 'auto'
  },
  link: {
    textDecoration: 'none',
    color: theme.palette.primary.contrastText,
    width: '100%'
  },
  center: {
    justifyContent: 'center'
  }
}));

interface Props extends MenuItemProps {
  isSelected: boolean;
  isShrinked: boolean;
}

const MenuItem = ({ icon, href, title, isSelected, isShrinked }: Props) => {
  const classes = useStyles();
  const { toggleDrawerState } = useContext(DrawerContext);
  const { isMobile } = useMobile();

  const iconComponent = icon ? (
    <Icon className={clsx(classes.icon, isSelected && classes.isSelected)} icon={icon} />
  ) : null;

  return (
    <WithLink href={href} className={classes.link} isSelected={isSelected}>
      <Box
        data-testid={`${convertToKebabCase(title)}-section-header`}
        className={clsx(classes.container, isSelected && classes.isSelected, isShrinked && classes.center)}
        onClick={isMobile ? toggleDrawerState : undefined}
      >
        <>
          {isShrinked ? (
            <Tooltip title={title} placement="right">
              <span className={classes.iconContainer}>{iconComponent}</span>
            </Tooltip>
          ) : (
            iconComponent
          )}
          {isShrinked ? null : (
            <Typography className={clsx(classes.title, isSelected && classes.isSelected)} variant="h3">
              {title}
            </Typography>
          )}
        </>
      </Box>
    </WithLink>
  );
};

export default memo(MenuItem);
